import React from 'react';
import styled from 'styled-components';
import { display, DisplayProps } from 'styled-system';
import { Product, Shipping } from '@packages/common';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { Translate, useTranslations } from '@packages/i18n';
import { Flex, Text, Icon, media, is, HAS_PSUDO_SKELETON_LIGHTER, truncate, TruncateProps } from '@packages/ui';

import { formattedPrice } from './utils';

export interface ShippingAndLocalInfoProps {
  shipping: Product['shipping'];
  isLocallyFiltered?: boolean;
  hideOnMobile?: boolean;
  isOffline: boolean;
}

const Wrapper = styled(Flex)<{ hideOnMobile: boolean }>`
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  max-height: 100%;
  ${is('hideOnMobile')`
    ${media.to.md`
      display: none;
    `}
   `}
`;

const LocalFilterText = styled(Text)<DisplayProps>`
  ${display};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const ShippingStatusForMobile = styled.span`
  display: flex;
  justify-content: flex-end;
  ${media.from.xl`
    display: none;
  `}
`;

export const ShippingStatusForDesktop = styled(Flex)`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
`;

const ShippingPriceTextStyled = styled(Text)`
  white-space: nowrap;
`;

const APP_TLD = getPublicRuntimeConfig('APP_TLD');

const ShippingPriceInfo: React.FC<Shipping> = ({ price }) => {
  const priceWithCurrency = formattedPrice(price, APP_TLD);
  const [shippingPrice] = useTranslations([{ key: 'shippingPrice', values: { price: priceWithCurrency } }]) as string[];
  return (
    <ShippingPriceTextStyled
      className={HAS_PSUDO_SKELETON_LIGHTER}
      tag="span"
      fontSize={[4]}
      color="sandDune"
      lineHeight={1.8}
    >
      {shippingPrice}
    </ShippingPriceTextStyled>
  );
};

const OfflineInfo = styled(Text)<TruncateProps>`
  ${truncate}
`;

const FreeShippingInfo: React.FC = () => (
  <Text tag="span" lineHeight={1.8} fontSize={[4]} color="success" className={HAS_PSUDO_SKELETON_LIGHTER}>
    <Translate translation="freeShipping" />
  </Text>
);

const ProductIsOfflineInfo: React.FC = () => (
  <OfflineInfo
    tag="span"
    lineHeight={[1.6, 1.6, 1.8]}
    linesToShow={1}
    fontSize={4}
    color="error"
    className={HAS_PSUDO_SKELETON_LIGHTER}
  >
    <Translate translation="productIsOffline" />
  </OfflineInfo>
);

export const LocallyFiltered: React.FC<{ hasContentLeft?: boolean }> = ({ hasContentLeft = false }) => (
  <Flex
    ml={hasContentLeft ? 2 : 0}
    alignItems="center"
    justifyContent="flex-end"
    overflow="hidden"
    className={HAS_PSUDO_SKELETON_LIGHTER}
  >
    <Icon name="markerFilled" size={14} color="primary" />
    <LocalFilterText
      display={['none', 'none', 'none', 'none', 'inline-block']}
      tag="span"
      fontSize={4}
      color="sandDune"
      ml={1}
    >
      <Translate translation="localProduct" />
    </LocalFilterText>
  </Flex>
);

export const ShippingAndLocalInfo: React.FC<ShippingAndLocalInfoProps> = ({
  shipping,
  isLocallyFiltered = false,
  hideOnMobile = false,
  isOffline = false,
}) => {
  const freeShipping = shipping && typeof shipping.price === 'number' && shipping.price === 0;
  const shippingCost = shipping && typeof shipping.price === 'number' && shipping.price > 0;
  return (
    // logic:
    // desktop precedence: fee shipping ? free shipping : cost of shipping | if locally filtered show it additionally
    // mobile precedence : freeshipping ? freeshipping : local filter : shipping cost
    <Wrapper hideOnMobile={isOffline ? false : hideOnMobile}>
      <ShippingStatusForDesktop justifyContent="space-between" alignItems="center">
        {isOffline ? (
          <ProductIsOfflineInfo />
        ) : freeShipping ? (
          <FreeShippingInfo />
        ) : shippingCost ? (
          <ShippingPriceInfo {...shipping} data-testid="shipping-price-info" />
        ) : null}
        {isLocallyFiltered && <LocallyFiltered hasContentLeft={isOffline || freeShipping || shippingCost} />}
      </ShippingStatusForDesktop>
    </Wrapper>
  );
};
